<template>
  <div
    :class="classTrigger"
    @click="toggle"
  >
    <slot name="trigger" />
  </div>
  <OverlayPanel
    ref="op"
    class="spm-overlay-panel"
    append-to="body"
    style="min-width: 240px;"
    :style="'min-width: '+ minPanelWidth +';' + (maxPanelWidth ? 'max-width: '+ maxPanelWidth +';' : '') + styleOverlayPanel"
    :breakpoints="{ '960px': '75vw' }"
    @hide="onHide"
    @show="onShow"
  >
    <div
      v-if="$slots.header"
      :class="classHeader"
    >
      <slot name="header" />
    </div>
    <div
      v-if="$slots.header"
      class="solid"
    />
    <div :class="classContent">
      <slot />
    </div>
    <div
      v-if="$slots.footer"
      class="solid"
    />
    <div
      v-if="$slots.footer"
      :class="classFooter"
    >
      <slot name="footer" />
    </div>
  </OverlayPanel>
</template>

<script lang="ts">
import {
  computed,
  defineComponent, ref, watch,
} from 'vue';
import OverlayPanel from 'primevue/overlaypanel';
import { useRoute } from 'vue-router';

export default defineComponent({
  name: 'SpmOverlayPanel',

  components: {
    OverlayPanel,
  },

  props: {
    classTrigger: {
      type: String,
      required: false,
      default: '',
    },

    classHeader: {
      type: String,
      required: false,
      default: '',
    },

    classContent: {
      type: String,
      required: false,
      default: '',
    },

    classFooter: {
      type: String,
      required: false,
      default: '',
    },

    closeOnRouteChange: {
      type: Boolean,
      required: false,
      default: true,
    },

    minPanelWidth: {
      type: String,
      required: false,
      default: '240px',
    },

    maxPanelWidth: {
      type: String,
      required: false,
      default: '',
    },

    styleOverlayPanel: {
      type: String,
      required: false,
      default: '',
    },

    closePanel: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  emits: ['onclose-panel'],

  setup(props, context) {
    const op = ref();
    const toggle = (event: Event) => op.value.toggle(event);
    const currentRoute = computed(() => useRoute());

    watch(() => currentRoute.value.name, () => {
      // if route changes, close the overlay panel
      if (props.closeOnRouteChange) {
        op.value.hide();
      }
    });

    watch(() => props.closePanel, () => {
      if (props.closePanel) {
        op.value.hide();
        context.emit('onclose-panel');
      }
    });

    const onHide = () => {
      if (props.closePanel) {
        context.emit('onclose-panel');
      }
    };

    const getOverlayEl = (): HTMLElement | null => {
      // PrimeVue exposes container on OverlayPanel instance
      const el = op.value?.container;
      if (el && el.nodeType === 1) return el;
      // Fallback: query visible overlay panels in DOM
      const panels = document.querySelectorAll('.p-overlaypanel');
      for (let i = 0; i < panels.length; i++) {
        const panel = panels[i] as HTMLElement;
        if (panel.offsetWidth > 0 && panel.offsetHeight > 0) {
          return panel;
        }
      }
      return null;
    };

    const onShow = () => {
      setTimeout(() => {
        const el = getOverlayEl();
        if (el) {
          const top = parseFloat(el.style.top) || 0;
          const left = parseFloat(el.style.left) || 0;
          el.style.position = 'fixed';
          el.style.top = `${top - window.scrollY}px`;
          el.style.left = `${left - window.scrollX}px`;
        }
      }, 10);
    };

    return {
      op,
      toggle,
      onHide,
      onShow,
    };
  },
});
</script>

<style lang="scss">
.spm-overlay-panel {
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.10), 0 2px 8px rgba(0, 0, 0, 0.06) !important;
  border: 1px solid #e5e7eb !important;
  border-radius: 12px !important;
  overflow: hidden;
  animation: spm-overlay-enter 0.2s cubic-bezier(0.4, 0, 0.2, 1);

  .p-overlaypanel-content {
    padding: 0 !important;
  }

  &:after, &:before {
    content: none !important;
  }

  div.solid {
    border-top: 1px solid #f0f0f0;
  }
}

@keyframes spm-overlay-enter {
  from {
    opacity: 0;
    transform: translateY(-6px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}
</style>
